import { m } from 'framer-motion';
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
// @mui
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import Stack from '@mui/material/Stack';
import Badge from '@mui/material/Badge';
import Drawer from '@mui/material/Drawer';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
import { useResponsive } from 'src/hooks/use-responsive';
// components
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { varHover } from 'src/components/animate';
import { useLocales } from 'src/locales';
import { useDispatch } from 'react-redux';
//
import { updateFaviconWithCount } from 'src/utils/notifications/update-favicon';
import { useServerRequest } from 'src/hooks/useServerRequest';
import { Notification } from 'src/types/notification';
import { addProject } from 'src/redux/slices/cautionSlice';
import { Project } from 'src/types/cautions';
import { useUnreadMessages } from 'src/context/UnreadMessagesContext';
import NotificationItem from './notification-item';

// ----------------------------------------------------------------------

export default function NotificationsPopover() {
  const drawer = useBoolean();
  const { t } = useLocales();
  const smUp = useResponsive('up', 'sm');
  const dispatch = useDispatch();
  const { makeRequest } = useServerRequest();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [totalUnRead, setTotalUnRead] = useState<number>(0);
  const { setNewCount } = useUnreadMessages();

  // Function to fetch notifications
  // const fetchNotifications = useCallback(async (signal: AbortSignal) => {
  //   try {
  //     const response = await makeRequest('/notifications', { method: 'GET', signal }, '-');
  //     if (response.success && response.data) {
  //       const { notifys, projects, newMessageCount } = response.data;

  //       if (notifys && notifys.length > 0) {
  //         setNotifications(notifys);
  //         const totalUnread = notifys.filter((notification: Notification) => notification.is_unread).length;
  //         setTotalUnRead(totalUnread);
  //       }

  //       if (projects && projects.length > 0) {
  //         projects.forEach((project: Project) => {
  //           dispatch(addProject(project));
  //         });
  //       }

  //       if (newMessageCount) setNewCount(newMessageCount);

  //     }
  //   } catch (error) {
  //     console.error("Error fetching notifications", error);
  //   }
  // }, [dispatch, makeRequest, setNewCount]);

  const fetchNotifications = useCallback(async (signal: AbortSignal) => {
    try {
      const response = await axios.get('/notifications', { signal });
      if (response.data.success && response.data.data) {
        const { notifys, projects, newMessageCount } = response.data.data;

        if (notifys && notifys.length > 0) {
          setNotifications(notifys);
          const totalUnread = notifys.filter((notification: Notification) => notification.is_unread).length;
          setTotalUnRead(totalUnread);
        }

        if (projects && projects.length > 0) {
          projects.forEach((project: Project) => {
            dispatch(addProject(project));
          });
        }

        if (newMessageCount) setNewCount(newMessageCount);
      }
    } catch (error) {
      console.error("Error fetching notifications", error);
    }
  }, [dispatch, setNewCount]);

  // Set up fetching and interval
  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    // Initial fetch
    fetchNotifications(signal);

    // Set up interval for fetching notifications every 15 seconds
    const interval = setInterval(() => {
      fetchNotifications(signal);
    }, 15000); // 15 seconds

    // Cleanup: Clear interval and abort the fetch on unmount
    return () => {
      clearInterval(interval); // Clear the interval
      controller.abort(); // Abort any ongoing fetch request
    };
  }, [fetchNotifications]);

  // Update favicon count whenever the total unread notifications change
  useEffect(() => {
    updateFaviconWithCount(totalUnRead);
  }, [totalUnRead]);

  // Mark all as read
  const handleMarkAsRead = async () => {
    const unreadNotifications = notifications.filter((notification) => notification.is_unread);
    const unreadIds = unreadNotifications.map((notification) => notification.id);

    const response = await makeRequest('/notifications/mark-as-read', { method: 'POST', data: { ids: unreadIds } }, '-');
    if (response) {
      setNotifications(
        notifications.map((notification) => ({
          ...notification,
          is_unread: false,
        }))
      );
      setTotalUnRead(0);
    }
  };

  const renderHead = (
    <Stack direction="row" alignItems="center" sx={{ py: 2, pl: 2.5, pr: 1, minHeight: 68 }}>
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        {t('common.topbar.notifications')}
      </Typography>

      {!!totalUnRead && (
        <Tooltip title="Mark all as read">
          <IconButton color="primary" onClick={handleMarkAsRead}>
            <Iconify icon="eva:done-all-fill" />
          </IconButton>
        </Tooltip>
      )}

      {!smUp && (
        <IconButton onClick={drawer.onFalse}>
          <Iconify icon="mingcute:close-line" />
        </IconButton>
      )}
    </Stack>
  );

  const renderList = (
    <Scrollbar>
      <List disablePadding>
        {notifications.map((notification) => (
          <NotificationItem key={notification.id} notification={notification} />
        ))}
      </List>
    </Scrollbar>
  );

  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        color={drawer.value ? 'primary' : 'default'}
        sx={{
          width: 35,
          height: 35,
        }}
        onClick={drawer.onTrue}
      >
        <Badge badgeContent={totalUnRead} color="error">
          <Iconify icon="solar:bell-bing-bold-duotone" width={24} />
        </Badge>
      </IconButton>

      <Drawer
        open={drawer.value}
        onClose={drawer.onFalse}
        anchor="right"
        slotProps={{
          backdrop: { invisible: true },
        }}
        PaperProps={{
          sx: { width: 1, maxWidth: 420 },
        }}
      >
        {renderHead}

        <Divider />

        {renderList}

        <Box sx={{ p: 1 }}>
          <Button fullWidth size="large">
            {t('common.topbar.view-all')}
          </Button>
        </Box>
      </Drawer>
    </>
  );
}
